import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { Checkbox, Select } from "../form";
export const PROJECTIONS = [
    { id: "globe", projection: "globe" },
    { id: "mercator", projection: "mercator" },
    { id: "equirectangular", projection: "equirectangular" },
    {
        id: "albers-north-america",
        projection: "albers",
        bounds: [
            [-96 - 40, 0],
            [-96 + 40, 90],
        ],
        initialView: { center: [-96, 37.5], zoom: 4 },
    },
    {
        id: "conic-australia",
        projection: {
            name: "lambertConformalConic",
            center: [134, 0],
            parallels: [-18, -36],
        },
        bounds: [
            [134 - 40, -90],
            [134 + 40, 0],
        ],
        initialView: { center: [134, -28], zoom: 4 },
    },
    {
        id: "conic-europe",
        projection: {
            name: "lambertConformalConic",
            center: [10, 52],
            parallels: [35, 65],
        },
        bounds: [
            [10 - 40, 0],
            [10 + 40, 90],
        ],
        initialView: { center: [10, 50], zoom: 4 },
    }, // European conic centred on 10, 52
];
const mapStyleOptions = [
    { value: "STREETS", label: "Streets" },
    { value: "OUTDOORS", label: "Outdoors" },
    { value: "LIGHT", label: "Light" },
    { value: "DARK", label: "Dark" },
    { value: "SATELLITE", label: "Satellite" },
    { value: "NAVIGATION-DAY", label: "Navigation Day" },
    { value: "NAVIGATION-NIGHT", label: "Navigation Night" },
];
const projectionOptions = [
    { label: "Globe", value: "globe" },
    { label: "Mercator", value: "mercator" },
    { label: "Equirectangular", value: "equirectangular" },
    { label: "Albers (North America)", value: "albers-north-america" },
    { label: "Conic (Australia)", value: "conic-australia" },
    { label: "Conic (Europe)", value: "conic-europe" },
];
const observationDurationOptions = [
    { label: "None", value: 0 },
    { label: "1 hour", value: 60 },
    { label: "2 hours", value: 2 * 60 },
    { label: "3 hours", value: 3 * 60 },
    { label: "6 hours", value: 6 * 60 },
    { label: "12 hours", value: 12 * 60 },
    { label: "24 hours", value: 24 * 60 },
    { label: "48 hours", value: 48 * 60 },
];
const forecastDurationOptions = [
    { label: "None", value: 0 },
    { label: "1 hour", value: 60 },
    { label: "2 hours", value: 2 * 60 },
    { label: "3 hours", value: 3 * 60 },
    { label: "6 hours", value: 6 * 60 },
    { label: "12 hours", value: 12 * 60 },
    { label: "24 hours", value: 24 * 60 },
    { label: "2 days", value: 48 * 60 },
    { label: "5 days", value: 120 * 60 },
    { label: "7 days", value: 168 * 60 },
    // { label: "10 days", value: 240 * 60 }, // not currently supported by Map Tile API
    // { label: "14 days", value: 336 * 60 },
];
const animationSpeedOptions = [
    { label: "Slow", value: "1000" },
    { label: "Normal", value: "333" },
    { label: "Fast", value: "200" },
    { label: "Faster", value: "100" },
    { label: "Fastest", value: "50" }, // 20 fps
];
const dwellOptions = [
    { label: "None", value: 1 },
    { label: "Short", value: 2 },
    { label: "Normal", value: 3 },
    { label: "Long", value: 5 },
    { label: "Longer", value: 10 },
    { label: "Longest", value: 20 },
];
export const MapSettings = ({ id, testId, selectedBaseMap = "DARK", initialBaseMapOptions, selectedProjection = "globe", selectedObservationDuration = 60, selectedForecastDuration = 60, selectedAnimationSpeed = 333, selectedDwell = 3, onBaseMapChange, onBaseMapOptionChange, onProjectionChange, onObservationDurationChange, onForecastDurationChange, onAnimationSpeedChange, onDwellChange, compact = false, heightCss, includeRegionalProjections, }) => {
    const containerClassName = useMemo(() => {
        let containerClassName = `bg-container-light dark:bg-dark-container-light text-primaryText dark:text-dark-primaryText flex flex-col`;
        if (compact) {
            containerClassName += " h-[22dvh] min-h-[300px]";
        }
        else {
            containerClassName += " h-[60dvh]";
        }
        return containerClassName;
    }, [heightCss, compact]);
    const style = useMemo(() => {
        const style = {};
        if (heightCss) {
            style["height"] = heightCss;
        }
        return style;
    }, [heightCss]);
    const useProjectionOptions = useMemo(() => {
        if (includeRegionalProjections) {
            return projectionOptions;
        }
        else {
            return projectionOptions.filter((p) => {
                const details = PROJECTIONS.find((pd) => pd.id === p.value);
                return (details === null || details === void 0 ? void 0 : details.bounds) === undefined;
            });
        }
    }, [includeRegionalProjections]);
    return (_jsx("div", { id: id, "data-test-id": testId, "data-eco-component": "eco-map-settings", className: containerClassName, style: style, children: _jsxs("div", { className: "flex-1 space-y-4 overflow-y-auto px-3 pb-3", children: [_jsx(Select, { id: "map-style", options: mapStyleOptions, label: "Base Map", name: "baseMap", onChange: onBaseMapChange, selected: selectedBaseMap }), onBaseMapOptionChange && (_jsx("div", { className: "flex w-full flex-wrap", children: initialBaseMapOptions === null || initialBaseMapOptions === void 0 ? void 0 : initialBaseMapOptions.map((option, index) => (_jsx("div", { className: "mb-2 flex-none basis-1/2 pl-2", children: _jsx(Checkbox, { id: option.id, value: option.id, label: option.label, checked: option.value === true, onChange: onBaseMapOptionChange }) }, `base-map-option-${index}`))) })), onProjectionChange && (_jsx(Select, { id: "projection", options: useProjectionOptions, label: "Projection", name: "projection", onChange: onProjectionChange, selected: selectedProjection })), _jsx(Select, { id: "observation-duration", options: observationDurationOptions, label: "Observation Duration", name: "observationDuration", onChange: (e) => {
                        onObservationDurationChange(e);
                    }, selected: selectedObservationDuration.toString() }), _jsx(Select, { id: "forecast-duration", options: forecastDurationOptions, label: "Forecast Duration", name: "forecastDuration", onChange: onForecastDurationChange, selected: selectedForecastDuration.toString() }), _jsx(Select, { id: "animation-speed", options: animationSpeedOptions, label: "Animation Speed", name: "animationSpeed", onChange: onAnimationSpeedChange, selected: selectedAnimationSpeed.toString() }), _jsx(Select, { id: "dwell", options: dwellOptions, label: "Dwell", name: "dwell", onChange: onDwellChange, selected: selectedDwell.toString() })] }) }));
};
